import React from "react";
import { Box, Container, Typography, Grid, Divider, Button, Link as MuiLink } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

export const BRAND = {
  teal: "#4fd1c5",
  tealDark: "#2dd4bf",
  ink: "#0f172a",
  inkSoft: "#1e293b",
  body: "#334155",
  muted: "#64748b",
  border: "#e2e8f0",
  bgSoft: "#f8fafc",
  bgPanel: "#ffffff",
  footerBg: "#0f172a",
};

const LOGO_SRC = "/landing/Images/Frame 45.svg";

export const sectionHeading = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 700,
  color: BRAND.ink,
  letterSpacing: "-0.01em",
  fontSize: { xs: "1.25rem", md: "1.5rem" },
  mb: 1.5,
  scrollMarginTop: 96,
};

export const subHeading = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  color: BRAND.ink,
  fontSize: "1rem",
  mt: 2.5,
  mb: 1,
};

export const bodyText = {
  fontFamily: '"Inter", sans-serif',
  color: BRAND.body,
  fontSize: "0.95rem",
  lineHeight: 1.7,
  mb: 1.5,
};

export const listSx = {
  fontFamily: '"Inter", sans-serif',
  color: BRAND.body,
  fontSize: "0.95rem",
  lineHeight: 1.75,
  pl: 3,
  mb: 1.5,
  "& li": { mb: 0.5 },
  "& li::marker": { color: BRAND.teal },
};

export function Section({ id, title, children }) {
  return (
    <Box component="section" id={id} sx={{ mb: { xs: 4, md: 5 } }}>
      <Typography component="h2" sx={sectionHeading}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export function Callout({ tone = "info", title, children }) {
  const palette = {
    info: { bg: "#ecfeff", border: "#a5f3fc", icon: BRAND.tealDark },
    warn: { bg: "#fffbeb", border: "#fde68a", icon: "#b45309" },
    danger: { bg: "#fef2f2", border: "#fecaca", icon: "#b91c1c" },
  }[tone];
  return (
    <Box
      sx={{
        bgcolor: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 3,
        p: { xs: 2, md: 2.5 },
        my: 2,
      }}
    >
      {title && (
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 700,
            color: palette.icon,
            fontSize: "0.9rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            mb: 1,
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ ...bodyText, mb: 0, color: BRAND.inkSoft }}>{children}</Box>
    </Box>
  );
}

export function MiniHeader({ onHome, secondaryLabel, secondaryHref }) {
  return (
    <Box
      component="header"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        bgcolor: "rgba(255,255,255,0.85)",
        backdropFilter: "saturate(180%) blur(10px)",
        WebkitBackdropFilter: "saturate(180%) blur(10px)",
        borderBottom: `1px solid ${BRAND.border}`,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
        }}
      >
        <Box
          component="a"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            onHome();
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            textDecoration: "none",
            color: BRAND.ink,
          }}
        >
          <Box
            component="img"
            src={LOGO_SRC}
            alt="MedLift"
            sx={{ width: 32, height: 32, borderRadius: 1.5 }}
          />
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 700,
              fontSize: "1.05rem",
              letterSpacing: "-0.01em",
              color: BRAND.ink,
            }}
          >
            MedLift
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}>
          {secondaryLabel && secondaryHref && (
            <MuiLink
              href={secondaryHref}
              sx={{
                display: { xs: "none", sm: "inline-block" },
                fontFamily: '"Inter", sans-serif',
                fontSize: "0.9rem",
                fontWeight: 500,
                color: BRAND.body,
                textDecoration: "none",
                "&:hover": { color: BRAND.ink },
              }}
            >
              {secondaryLabel}
            </MuiLink>
          )}
          <Button
            href="/"
            onClick={(e) => {
              e.preventDefault();
              onHome();
            }}
            sx={{
              bgcolor: BRAND.teal,
              color: BRAND.ink,
              textTransform: "none",
              fontFamily: '"Inter", sans-serif',
              fontWeight: 600,
              fontSize: "0.875rem",
              px: 2,
              py: 0.85,
              borderRadius: 2,
              boxShadow: "none",
              "&:hover": { bgcolor: BRAND.tealDark, boxShadow: "none" },
            }}
          >
            Torna al sito
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export function MiniFooter({ contactEmail, onNavigate }) {
  const year = new Date().getFullYear();
  const linkSx = {
    fontFamily: '"Inter", sans-serif',
    fontSize: "0.9rem",
    color: "#cbd5e1",
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": { color: BRAND.teal },
  };
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: BRAND.footerBg,
        color: "#e2e8f0",
        pt: { xs: 5, md: 7 },
        pb: 4,
        mt: { xs: 6, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
              <Box
                component="img"
                src={LOGO_SRC}
                alt="MedLift"
                sx={{ width: 36, height: 36, borderRadius: 1.5 }}
              />
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 700,
                  fontSize: "1.2rem",
                  color: "#ffffff",
                  letterSpacing: "-0.01em",
                }}
              >
                MedLift
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: "0.9rem",
                color: "#94a3b8",
                lineHeight: 1.6,
                maxWidth: 380,
              }}
            >
              La piattaforma svizzera che connette strutture sanitarie e
              trasportatori sanitari in tempo reale.
            </Typography>
          </Grid>

          <Grid item xs={6} md={3}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: "0.78rem",
                color: "#ffffff",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: 1.5,
              }}
            >
              Navigazione
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box component="a" sx={linkSx} onClick={() => onNavigate("/")}>Home</Box>
              <Box component="a" sx={linkSx} onClick={() => onNavigate("/about-us")}>Chi siamo</Box>
              <Box component="a" sx={linkSx} onClick={() => onNavigate("/contact-us")}>Contatti</Box>
            </Box>
          </Grid>

          <Grid item xs={6} md={4}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: "0.78rem",
                color: "#ffffff",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: 1.5,
              }}
            >
              Legale
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box component="a" sx={linkSx} onClick={() => onNavigate("/privacy-policy")}>Privacy Policy</Box>
              <Box component="a" sx={linkSx} onClick={() => onNavigate("/term-condition")}>Termini e Condizioni</Box>
              <MuiLink href={`mailto:${contactEmail}`} sx={linkSx}>{contactEmail}</MuiLink>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: "#1e293b" }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: "0.85rem",
              color: "#94a3b8",
            }}
          >
            &copy; {year} MedLift. Tutti i diritti riservati.
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: "0.8rem",
              color: "#64748b",
            }}
          >
            Made in Switzerland · Conforme nLPD
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export function usePublicNav() {
  const navigate = useNavigate();
  return (path) => {
    if (path === "/") {
      window.location.href = "/";
    } else {
      navigate(path);
    }
  };
}

export function useContactEmail() {
  const settings = useSelector((state) => state.settingsdata.settings);
  return (settings && settings.contact_email) || "privacy@medlift.app";
}
